import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHistory, faAward, faEye, faBullseye, faAnchor, faShip, faQuoteLeft } from "@fortawesome/free-solid-svg-icons";
import LogoLoader from "../components/LogoLoader";

// Import local images
import { AboutHero, AboutHeritage } from "../assets/data/Imagedata";

import seoData from "../assets/data/seo.json";

const Aboutus = () => {
  const [loading, setLoading] = useState(true);

  const { title, description, keywords, canonical, ogImage } = seoData.aboutus;

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 2500);
    return () => clearTimeout(timer);
  }, []);

  if (loading) return <LogoLoader isLoading={loading} />;

  return (
    <div className="bg-slate-50">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="keywords" content={keywords} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:url" content={canonical} />
        <link rel="canonical" href={canonical} />
      </Helmet>

      {/* Hero Section */}
      <section className="relative h-[40vh] md:h-[50vh] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center transition-transform duration-[2s] scale-110 hover:scale-100"
          style={{
            backgroundImage: `url(${AboutHero})`,
          }}
        >
          <div className="absolute inset-0 bg-black/40 backdrop-blur-[1px]"></div>
        </div>
        <div className="relative z-10 text-center px-4 max-w-5xl">
          <h1 className="text-[34px] font-TuskerGrotesk font-bold text-white mb-2 tracking-widest uppercase drop-shadow-2xl">
            ABOUT OUR <span className="text-primary">LEGACY</span>
          </h1>
          <div className="w-16 h-1 bg-secondary mx-auto rounded-full shadow-lg"></div>
          <p className="mt-4 text-[16px] text-white/90 font-medium tracking-wide uppercase max-w-2xl mx-auto drop-shadow-lg leading-tight text-white">
            Navigating Industry Standards Since 1996
          </p>
        </div>
      </section>

      {/* Heritage & Expertise */}
      <section className="py-12 bg-white overflow-hidden">
        <div className="container mx-auto px-4 lg:px-20">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div className="space-y-4">
              <div className="inline-flex items-center space-x-2 text-primary font-bold uppercase tracking-[0.2em] text-[12px]">
                <FontAwesomeIcon icon={faHistory} />
                <span>Our Heritage & Expertise</span>
              </div>
              <h2 className="text-[34px] font-TuskerGrotesk font-bold text-black leading-tight tracking-wider uppercase">
                THREE DECADES OF <br /><span className="text-primary italic">MARITIME MASTERY</span>
              </h2>
              <div className="w-16 h-1 bg-primary/20 rounded-full"></div>
              <div className="space-y-4 text-slate-600 text-[14px] leading-relaxed">
                <p>
                  Our journey began in 1996 at the ground level of the shipping industry, where we mastered the trade step-by-step. Over nearly three decades, we have expanded our footprint to 12 major Indian ports, gaining vast experience by working alongside industry giants. This deep, hands-on knowledge of port operations forms the strong foundation upon which Shivam Shipping and Logistics-SCM is built.
                </p>

              </div>
            </div>
            <div className="relative">
              <div className="aspect-[16/9] rounded-3xl overflow-hidden shadow-xl border-4 border-white">
                <img
                  src={AboutHeritage}
                  alt="Vessel Operations"
                  className="w-full h-full object-cover transform transition-transform hover:scale-110 duration-1000"
                />
              </div>
              <div className="absolute -bottom-4 -right-4 bg-primary p-4 rounded-xl shadow-xl hidden lg:block border border-white/20">
                <div className="text-[34px] font-black text-white mb-0">30</div>
                <div className="text-white/80 text-[10px] uppercase font-bold tracking-[0.2em] leading-tight">
                  Years of Proven<br />Performance
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Commitment & Values */}
      <section className="py-16 bg-gradient-to-b from-slate-50 to-white relative">
        <div className="container mx-auto px-4 lg:px-20">
          <div className="text-center max-w-4xl mx-auto mb-10">
            <div className="inline-flex items-center space-x-2 text-primary font-bold uppercase tracking-[0.2em] text-[12px] mb-4">
              <span className="w-8 h-[2px] bg-primary"></span>
              <FontAwesomeIcon icon={faAward} />
              <span>Our Commitment to Excellence</span>
              <span className="w-8 h-[2px] bg-primary"></span>

            </div>

            <div className="space-y-4 text-slate-600 text-[14px] leading-relaxed">
              <p>
                We are driven by a legacy of proven performance, highlighted by recognitions such as the 'Best Employee' award for handling Indian Government cargo and the 'Star Performer Award' for developing innovative coal logistics software. Today, our operations remain anchored in our core values of safety, service, honesty, and unwavering client loyalty, ensuring every partnership is defined by our dedication and passion.
              </p>
            </div>

          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { label: "SAFETY", desc: "Uncompromising security." },
              { label: "SERVICE", desc: "Seamless logistics." },
              { label: "HONESTY", desc: "Transparent practices." },
              { label: "LOYALTY", desc: "Client commitment." },
            ].map((val, i) => (
              <div key={i} className="group p-6 rounded-2xl bg-white border border-slate-100 hover:bg-primary transition-all duration-500 shadow-sm hover:shadow-xl hover:-translate-y-1">
                <div className="text-[18px] font-bold text-black mb-2 group-hover:text-white transition-colors tracking-widest leading-none uppercase">{val.label}</div>
                <p className="text-slate-400 text-[10px] group-hover:text-white/70 transition-colors uppercase tracking-[0.2em] font-bold leading-none">{val.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Founder's Message - Premium Multi-color Section */}
      <section className="py-16 bg-primary text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-primary/10 rounded-full blur-[100px] -mr-32 -mt-32" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-secondary/5 rounded-full blur-[100px] -ml-32 -mb-32" />

        <div className="container mx-auto px-4 lg:px-20 relative z-10">
          <div className="max-w-6xl mx-auto">
            <div className="bg-white/5 backdrop-blur-sm p-8 md:p-12 rounded-[2.5rem] border border-white/10 shadow-2xl">
              <div className="flex flex-col lg:flex-row gap-10 items-start">
                <div className=" space-y-6">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-1 bg-white border-[0.2px] border-white"></div>
                    <h3 className="text-[18px] font-bold tracking-[0.2em] uppercase text-white leading-none">Founder’s Message</h3>
                  </div>
                  <div className="relative space-y-4 ">
                    <FontAwesomeIcon icon={faQuoteLeft} className="absolute -top-6 -left-6 text-6xl text-white/5" />
                    <p className="text-[18px] md:text-[18px] font-light leading-tight italic text-slate-100 font-serif">
                      Inspired by a family legacy of Mrs.Nutan Mishra in the Merchant Navy, our founder established Shivam Shipping and Logistics-SCM with a clear vision: to bring transparency and security to the logistics trade. While the industry is complex, our operator and his approach are grounded in a simple philosophy: business must be sustainable and serve the greater good of society.

                    </p>
                    <p className="text-[18px] md:text-[18px] font-light leading-tight italic text-slate-100 font-serif">
                      This commitment to service is most visible during critical challenges. In an industry where nature often dictates the rules, our operator’s leadership has been pivotal. During a severe cyclone crisis in Gujarat, he personally directed operations to secure a vessel, preventing heavy equipment from being lost to the sea.
                    </p>
                    <p className="text-[18px] md:text-[18px] font-light leading-tight italic text-slate-100 font-serif">
                      In a simultaneous feat of crisis management, his team successfully salvaged 80,000 Metric Tons of fertilizer that was at risk of destruction, ensuring that vital resources were not wasted. This hands-on, fearless approach defines the leadership at Shivam Shipping and Logistics-SCM.

                    </p>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 lg:px-20">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Vision */}
            <div className="group bg-slate-50 p-10 md:p-12 rounded-[2.5rem] border border-slate-100 transition-all duration-500 hover:bg-white hover:shadow-xl hover:border-primary/20">
              <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-primary transition-all transform group-hover:-rotate-6">
                <FontAwesomeIcon icon={faEye} className="text-primary text-2xl group-hover:text-white transition-colors" />
              </div>
              <h3 className="text-[34px] font-TuskerGrotesk font-bold text-black mb-4 group-hover:text-primary transition-colors uppercase tracking-widest leading-none">Our Vision</h3>
              <p className="text-slate-600 text-[14px] leading-relaxed font-medium">
                To establish Shivam Shipping & Logistics-SCM as India’s most dependable and constant logistics partner. We envision a future where our brand is synonymous with unwavering honesty and relentless dedication, setting a new benchmark for integrity and service consistency across the nation.
              </p>
            </div>

            {/* Mission */}
            <div className="group bg-slate-50 p-10 md:p-12 rounded-[2.5rem] border border-slate-100 transition-all duration-500 hover:bg-white hover:shadow-xl hover:border-primary/20">
              <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-primary transition-all transform group-hover:rotate-6">
                <FontAwesomeIcon icon={faBullseye} className="text-primary text-2xl group-hover:text-white transition-colors" />
              </div>
              <h3 className="text-[34px] font-TuskerGrotesk font-bold text-black mb-4 group-hover:text-primary transition-colors uppercase tracking-widest leading-none">Our Mission</h3>
              <p className="text-slate-600 text-[14px] leading-relaxed font-medium">
                We prioritize client satisfaction above all else. We are committed to delivering seamless, transparent solutions that go beyond the transaction. By consistently exceeding expectations, we aim to nurture enduring relationships built on mutual trust and service excellence.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Closing Decoration */}
      <footer className="py-12 bg-slate-50 border-t border-slate-200">
        <div className="container mx-auto px-4 text-center">
          <div className="w-px h-16 bg-gradient-to-b from-primary to-transparent mx-auto mb-8"></div>
          <FontAwesomeIcon icon={faAnchor} className="text-primary/20 text-4xl mb-6 animate-pulse" />
          <h2 className="text-[18px] font-bold text-slate-400 uppercase tracking-[0.5rem] mb-2">ESTD 1996</h2>
          <p className="text-slate-400 text-[10px] font-bold tracking-[0.3rem] uppercase leading-none">Shivam Shipping and Logistics-SCM</p>
        </div>
      </footer>
    </div>
  );
};

export default Aboutus;

